import { useEffect, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { prizeManagementV1, PRIZE_CONSTANTS } from '../../shared/api'
import promoSuccessImg from '../../assets/img/promo-success.png'
import { COUNTRY_CODES, getNpShoppingBuyInUaUrl } from '../../config/constants'
import {
  ButtonElement,
  ButtonLocation,
  FormElement,
  trackButtonClick,
  trackFormSent,
} from '../../utils/analyticsEvents'
import { extractDigits, formatWithMask, maskDigitCount } from '../../utils/phoneMask'
import { Button } from '../Button/Button'
import { Checkbox } from '../Checkbox/Checkbox'
import { CountryCodeSelect } from '../CountryCodeSelect/CountryCodeSelect'
import { Modal } from '../Modal/Modal'
import { StoreButtons } from '../StoreButtons/StoreButtons'
import styles from './PromoCodeModal.module.scss'

interface PromoFormValues {
  consent: boolean
  countryCode: string
  phone: string
}

interface PromoCodeModalProps {
  onClose: () => void
  onHowItWorks: () => void
}

export const PromoCodeModal = ({ onClose, onHowItWorks }: PromoCodeModalProps) => {
  const { t, i18n } = useTranslation()
  const [submitted, setSubmitted] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [prizeId, setPrizeId] = useState<string | null>(null)
  const [prizeLoadFailed, setPrizeLoadFailed] = useState(false)
  const {
    control,
    formState: { errors },
    getValues,
    handleSubmit,
    register,
    setError,
    setValue,
    watch,
  } = useForm<PromoFormValues>({
    defaultValues: { consent: false, countryCode: COUNTRY_CODES[0].code, phone: '' },
  })
  const countryCode = watch('countryCode')
  const country = COUNTRY_CODES.find((item) => item.code === countryCode) ?? COUNTRY_CODES[0]

  useEffect(() => {
    prizeManagementV1
      .getActiveLandingPrize({ countryCode: PRIZE_CONSTANTS.COUNTRY_CODE })
      .then((prize) => {
        setPrizeId(prize.prizeId)
        setPrizeLoadFailed(false)
      })
      .catch(() => {
        setPrizeLoadFailed(true)
      })
  }, [])

  const onSubmit = async (values: PromoFormValues) => {
    if (!prizeId) {
      setError('phone', {
        message: t('promoModal.errors.somethingWentWrong'),
        type: 'apiError',
      })
      return
    }

    setIsLoading(true)
    try {
      const phoneWithCountry = `${values.countryCode}${extractDigits(values.phone)}`
      const result = await prizeManagementV1.assignLandingDeliveryPromoCodePrize({
        countryCode: PRIZE_CONSTANTS.COUNTRY_CODE,
        phoneNumber: phoneWithCountry,
        prizeId,
      })

      if (result.success) {
        trackFormSent(FormElement.GetPromo)
        setSubmitted(true)
      } else {
        setError('phone', {
          message: t('promoModal.errors.somethingWentWrong'),
          type: 'apiError',
        })
      }
    } catch {
      setError('phone', {
        message: t('promoModal.errors.somethingWentWrong'),
        type: 'apiError',
      })
    } finally {
      setIsLoading(false)
    }
  }

  if (submitted) {
    return (
      <Modal labelledBy="promo-success-title" onClose={onClose}>
        <div className={styles.success}>
          <img alt="" height="200" src={promoSuccessImg} width="200" />
          <h2 className={styles.successTitle} id="promo-success-title">
            {t('promoModal.success.title')}
          </h2>
          <p className={styles.successText}>{t('promoModal.success.text')}</p>
          <div className={styles.successActions}>
            <Button
              href={getNpShoppingBuyInUaUrl(i18n.language)}
              variant="green"
              onClick={() => {
                trackButtonClick(ButtonElement.ActivateInApp, ButtonLocation.PromoGranted)
              }}
            >
              {t('promoModal.success.activate')}
            </Button>
            <Button
              variant="white"
              onClick={() => {
                trackButtonClick(ButtonElement.OpenInstruction, ButtonLocation.PromoGranted)
                onHowItWorks()
              }}
            >
              {t('promoModal.success.how')}
            </Button>
          </div>
        </div>
      </Modal>
    )
  }

  return (
    <Modal labelledBy="promo-modal-title" onClose={onClose}>
      <div className={styles.modal}>
        {prizeLoadFailed ? (
          <div className={styles.errorBanner} role="alert">
            {t('promoModal.errors.somethingWentWrong')}
          </div>
        ) : null}
        <h2 className={styles.title} id="promo-modal-title">
          {t('promoModal.title')}
        </h2>
        <p className={styles.subtitle}>{t('promoModal.subtitle')}</p>
        {/* eslint-disable-next-line @typescript-eslint/no-misused-promises */}
        <form noValidate onSubmit={handleSubmit(onSubmit)}>
          <div className={styles.fields}>
            <CountryCodeSelect
              value={countryCode}
              onChange={(code) => {
                setValue('countryCode', code)
                const nextMask =
                  COUNTRY_CODES.find((item) => item.code === code)?.mask ?? COUNTRY_CODES[0].mask
                setValue('phone', formatWithMask(getValues('phone'), nextMask))
              }}
            />
            <div className={styles.phone}>
              <label className="visually-hidden" htmlFor="promo-phone">
                {t('promoModal.phoneLabel')}
              </label>
              <Controller
                control={control}
                name="phone"
                render={({ field }) => (
                  <input
                    className={`${styles.input}${errors.phone ? ` ${styles.invalid}` : ''}`}
                    id="promo-phone"
                    inputMode="numeric"
                    maxLength={country.mask.length}
                    name={field.name}
                    placeholder={country.mask}
                    type="tel"
                    value={field.value}
                    onBlur={field.onBlur}
                    onChange={(event) => {
                      field.onChange(formatWithMask(event.target.value, country.mask))
                    }}
                  />
                )}
                rules={{
                  validate: (value) => {
                    const digits = extractDigits(value)
                    if (digits.length === 0) {
                      return t('promoModal.errors.required')
                    }
                    if (digits.length !== maskDigitCount(country.mask)) {
                      return t('promoModal.errors.invalid')
                    }
                    return true
                  },
                }}
              />
              {errors.phone ? (
                <p className={styles.error} role="alert">
                  {errors.phone.message}
                </p>
              ) : null}
            </div>
          </div>
          <div className={styles.consent}>
            <Checkbox
              error={Boolean(errors.consent)}
              label={t('promoModal.consent')}
              {...register('consent', {
                required: t('promoModal.errors.consent'),
              })}
            />
            {errors.consent ? (
              <p className={styles.error} role="alert">
                {errors.consent.message}
              </p>
            ) : null}
          </div>
          <Button className={styles.submit} disabled={isLoading || !prizeId} type="submit" variant="green">
            {isLoading ? t('promoModal.loading') : t('promoModal.submit')}
          </Button>
        </form>
        <button
          className={styles.how}
          type="button"
          onClick={() => {
            trackButtonClick(ButtonElement.OpenInstruction, ButtonLocation.GetPromoWindow)
            onHowItWorks()
          }}
        >
          {t('promoModal.how')}
        </button>
        <hr className={styles.divider} />
        <div className={styles.download}>
          <p className={styles.hint}>{t('promoModal.download')}</p>
          <StoreButtons className={styles.stores} location={ButtonLocation.GetPromoWindow} />
        </div>
      </div>
    </Modal>
  )
}
